import { Clock, HeroBackdrop, StatusBadge } from '../../../../modules/design-system';
import { isOfferAvailable, statusLabel } from '../offerStatus';
import { HeroOverlay } from './HeroOverlay';
import { HeroPack } from './HeroPack';

export interface HeroSectionProps {
  packImageUrl: string | null;
  title: string;
  /**
   * Hero-animation Lottie URL from the backend. Currently we only render
   * the pack image; the backdrop / overlay slots are in the DOM but empty.
   * When the designer delivers the file(s), wire it in through these two
   * props without changing the surrounding layout.
   */
  heroAnimationUrl: string | null;
  overlayAnimationUrl?: string | null;
  /** `pool_offer.status`. When ≠ 'available' the hero dims and a status badge appears. */
  status?: string;
}

/**
 * Three stacked layers:
 *   1. HeroBackdrop  — concentric waves / ambient animation (Lottie TBD)
 *   2. HeroPack      — centred pack PNG
 *   3. HeroOverlay   — per-sport decorative motion (Lottie TBD, optional)
 *
 * The container establishes a positioning context, fills its parent, and
 * vertically centres the pack.
 *
 * Non-available states (Figma `151:2900` "Restoring"): the pack is dimmed
 * by a darkening overlay and a status pill is anchored near the top of
 * the hero.
 */
export function HeroSection({
  packImageUrl,
  title,
  heroAnimationUrl,
  overlayAnimationUrl = null,
  status,
}: HeroSectionProps) {
  const available = isOfferAvailable(status);

  return (
    // `overflow-hidden` was clipping the HeroPack's `drop-shadow(0 28px ...)`
    // — on mobile the slot fits the pack so tightly that the shadow had
    // nowhere to fall and the pack read as a hard-edged sticker rather
    // than a floating object. HeroBackdrop / HeroOverlay are absolutely
    // positioned with `inset-0`, so they're still bound to this container
    // without the clip; only the pack's halo and Lottie spillover (if any
    // ever land) can paint outside, which is what we want.
    <div
      data-hero
      className="relative flex h-full min-h-82 w-full items-center justify-center"
    >
      <HeroBackdrop animationUrl={heroAnimationUrl} />
      <div className={'relative z-10 ' + (available ? '' : 'grayscale blur-[4px]')}>
        <HeroPack imageUrl={packImageUrl} title={title} />
      </div>
      <HeroOverlay animationUrl={overlayAnimationUrl} />

      {!available && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <StatusBadge label={statusLabel(status)} icon={<Clock size={24} />} />
        </div>
      )}
    </div>
  );
}
